/**
 * Non-reactive accessors for the current user + bearer token. Prefer the
 * hooks (`useAuthUser`, `useBearerToken`) in React components; use these
 * for one-off reads outside the render tree (e.g. building a fetch request).
 */

import { getAuthInstance } from './client';
import { mapFirebaseError } from './errors';
import { type CurrentUser } from './types';

export function getCurrentUser(): CurrentUser | null {
  const user = getAuthInstance().currentUser;
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    emailVerified: user.emailVerified,
    displayName: user.displayName,
    photoURL: user.photoURL,
  };
}

/**
 * Fetches a fresh Firebase ID token for the current user, forcing a refresh
 * when the cached token is near expiry. Returns `null` if no user is signed in.
 */
export async function getBearerToken(): Promise<string | null> {
  const user = getAuthInstance().currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch (err) {
    throw mapFirebaseError(err);
  }
}

export async function reloadCurrentUser(): Promise<CurrentUser | null> {
  const user = getAuthInstance().currentUser;
  if (!user) return null;
  try {
    await user.reload();
  } catch (err) {
    throw mapFirebaseError(err);
  }
  return getCurrentUser();
}
